import * as Yup from 'yup';

export const DEFAULT_CUSTOMER_ID_LENGTH = 10;
export const LESCO_CUSTOMER_ID_MIN_LENGTH = 7;
export const LESCO_CUSTOMER_ID_MAX_LENGTH = 10;
export const REFERENCE_NO_LENGTH = 14;

export const isLescoCompany = (companyCode) =>
  String(companyCode ?? '').trim() === '11';

export const getCustomerIdMinLength = (companyCode) =>
  isLescoCompany(companyCode)
    ? LESCO_CUSTOMER_ID_MIN_LENGTH
    : DEFAULT_CUSTOMER_ID_LENGTH;

export const getCustomerIdMaxLength = (companyCode) =>
  isLescoCompany(companyCode)
    ? LESCO_CUSTOMER_ID_MAX_LENGTH
    : DEFAULT_CUSTOMER_ID_LENGTH;

/** Max digits allowed in the customer ID input field. */
export const getCustomerIdInputMaxLength = (companyCode) =>
  getCustomerIdMaxLength(companyCode);

/** @deprecated Use getCustomerIdInputMaxLength for input fields or getCustomerIdMinLength for validation. */
export const getCustomerIdLength = getCustomerIdInputMaxLength;

export const getCustomerIdValidationMessage = (companyCode, variant = 'default') => {
  if (isLescoCompany(companyCode)) {
    return variant === 'short'
      ? `Customer ID must be ${LESCO_CUSTOMER_ID_MIN_LENGTH} to ${LESCO_CUSTOMER_ID_MAX_LENGTH} digits.`
      : `Customer ID. must be ${LESCO_CUSTOMER_ID_MIN_LENGTH} to ${LESCO_CUSTOMER_ID_MAX_LENGTH} digits.`;
  }

  return variant === 'short'
    ? `Customer ID must be ${DEFAULT_CUSTOMER_ID_LENGTH} digits.`
    : `Customer ID. must be ${DEFAULT_CUSTOMER_ID_LENGTH} digits.`;
};

export const isValidCustomerIdLength = (customerId, companyCode) => {
  const id = String(customerId ?? '').trim();
  if (!/^\d+$/.test(id)) {
    return false;
  }

  if (isLescoCompany(companyCode)) {
    return (
      id.length >= LESCO_CUSTOMER_ID_MIN_LENGTH &&
      id.length <= LESCO_CUSTOMER_ID_MAX_LENGTH
    );
  }

  return id.length === DEFAULT_CUSTOMER_ID_LENGTH;
};

export const isValidConsumerIdOrReferenceNo = (value, companyCode) => {
  const normalized = String(value ?? '').trim();
  return (
    isValidCustomerIdLength(normalized, companyCode) ||
    normalized.length === REFERENCE_NO_LENGTH
  );
};

export const applyCustomerIdLengthValidation = (
  schema,
  companyCode,
  variant = 'default',
) => {
  const message = getCustomerIdValidationMessage(companyCode, variant);

  if (isLescoCompany(companyCode)) {
    return schema
      .min(LESCO_CUSTOMER_ID_MIN_LENGTH, message)
      .max(LESCO_CUSTOMER_ID_MAX_LENGTH, message);
  }

  return schema
    .min(DEFAULT_CUSTOMER_ID_LENGTH, message)
    .max(DEFAULT_CUSTOMER_ID_LENGTH, message);
};

export const buildCustomerIdYupRule = (companyCode, variant = 'default') =>
  applyCustomerIdLengthValidation(Yup.string(), companyCode, variant).required(
    'Enter consumer Id.',
  );

/** DISCO company code embedded in a 10-digit customer ID (e.g. 1131934803 → 13). */
export const extractCompanyCodeFromCustomerId = (customerId) => {
  const id = String(customerId ?? '').trim();
  if (!/^\d+$/.test(id) || id.length < 3) {
    return '';
  }
  return id.slice(1, 3);
};

/**
 * DISCO company code from customer ID or 14-digit reference number.
 * Customer ID: digits at index 1–2; reference no: digits at index 3–4.
 */
export const extractCompanyCodeFromConsumerSearchId = (searchId) => {
  const id = String(searchId ?? '').trim();
  if (!id) {
    return '';
  }

  if (/^\d+$/.test(id) && id.length >= 3 && id.length <= 10) {
    return extractCompanyCodeFromCustomerId(id);
  }

  if (id.length >= 5) {
    return id.slice(3, 5);
  }

  return '';
};
